import { useCallback, useState } from "react";

import { isAPIError, readServiceError } from "@/core/services";
import {
  ignorarConflito,
  LocalIndisponivelError,
  remanejarLocal,
} from "@/modules/carga-de-trabalho";
import type {
  IgnorarConflitoRequest,
  RemanejarLocalRequest,
  RemanejarLocalResult,
} from "@/modules/carga-de-trabalho";

export const RESOLVER_FORBIDDEN_MESSAGE =
  "Você não tem permissão para resolver conflitos de alocação.";
export const REMANEJAR_NOT_FOUND_MESSAGE =
  "A alocação não foi encontrada. Atualize a carga de trabalho e tente novamente.";
export const REMANEJAR_ERROR_MESSAGE = "Não foi possível remanejar o local. Tente novamente.";
export const IGNORAR_ALREADY_REGISTERED_MESSAGE =
  "Este conflito já foi registrado como tratado. Atualize a carga de trabalho.";
export const IGNORAR_ERROR_MESSAGE = "Não foi possível registrar o conflito. Tente novamente.";

/** `localIndisponivel`: 409 `LOCAL_INDISPONIVEL`; `invalid`: 400 (vai no campo justificativa). */
export type ResolverFailureKind =
  "localIndisponivel" | "conflict" | "forbidden" | "notFound" | "invalid" | "error";

export interface ResolverFailure {
  ok: false;
  kind: ResolverFailureKind;
  message: string;
}

export type ResolverResult<T = void> = ({ ok: true } & { data: T }) | ResolverFailure;

interface FailureMessages {
  conflict: string;
  notFound: string;
  error: string;
}

function toFailure(error: unknown, messages: FailureMessages): ResolverFailure {
  const failure = (kind: ResolverFailureKind, message: string): ResolverFailure => ({
    ok: false,
    kind,
    message,
  });

  if (error instanceof LocalIndisponivelError) return failure("localIndisponivel", error.message);
  if (!isAPIError(error)) return failure("error", messages.error);

  switch (error.status) {
    case 409:
      return failure("conflict", messages.conflict);
    case 403:
      return failure("forbidden", RESOLVER_FORBIDDEN_MESSAGE);
    case 404:
      return failure("notFound", messages.notFound);
    case 400:
      // O bff pode embrulhar o 400 do core: vale a mensagem do core.
      return failure("invalid", readServiceError(error).message || messages.error);
    default:
      return failure("error", messages.error);
  }
}

/** Nunca rejeita: devolve o resultado para o modal decidir onde mostrar o erro. */
function useResolverAction<P, R>(action: (params: P) => Promise<R>, messages: FailureMessages) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { conflict, notFound, error: errorMessage } = messages;

  const submit = useCallback(
    async (params: P): Promise<ResolverResult<R>> => {
      setIsSubmitting(true);
      try {
        return { ok: true, data: await action(params) };
      } catch (error) {
        return toFailure(error, { conflict, notFound, error: errorMessage });
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, conflict, notFound, errorMessage],
  );

  return { submit, isSubmitting };
}

export interface RemanejarLocalParams extends RemanejarLocalRequest {
  alocacaoId: string;
}

function remanejar({ alocacaoId, ...body }: RemanejarLocalParams): Promise<RemanejarLocalResult> {
  return remanejarLocal(alocacaoId, body);
}

export function useRemanejarLocal() {
  const { submit, isSubmitting } = useResolverAction(remanejar, {
    conflict: REMANEJAR_ERROR_MESSAGE,
    notFound: REMANEJAR_NOT_FOUND_MESSAGE,
    error: REMANEJAR_ERROR_MESSAGE,
  });
  return { remanejar: submit, isSubmitting };
}

export function useIgnorarConflitoLocal() {
  const { submit, isSubmitting } = useResolverAction(
    (params: IgnorarConflitoRequest) => ignorarConflito(params),
    {
      conflict: IGNORAR_ALREADY_REGISTERED_MESSAGE,
      notFound: IGNORAR_ERROR_MESSAGE,
      error: IGNORAR_ERROR_MESSAGE,
    },
  );
  return { ignorar: submit, isSubmitting };
}
